import React, { useState, useCallback, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
    X,
    Home,
    Calendar,
    DollarSign,
    MapPin,
    Car,
    FileText,
    Gavel,
    Clock,
    Search,
    HeartHandshake,
    Bed,
    Bath,
    Square,
} from 'lucide-react';
import {
    type CanonicalFields,
    type CanonicalSummary,
    formatField,
    extractValue,
    formatSqft,
    formatBaths,
} from '@/utils/canonicalFields';

export interface PipelineProperty {
    id: string;
    propertyId: string;
    address: string;
    city: string;
    status: string;
    auctionDate?: string;
    caseFiledDate?: string;
    openingBid?: number;
    stage: PipelineStage;
    createdAt: string;
    updatedAt: string;
    // Canonical fields with provenance (Phase 5B.2D)
    canonical?: CanonicalFields;
    summary?: CanonicalSummary;
}

export type PipelineStage =
    | 'interested'
    | 'researching'
    | 'drive_by'
    | 'title'
    | 'offer'
    | 'under_contract'
    | 'pass';

interface PipelineBoardProps {
    properties: PipelineProperty[];
    onStageChange: (propertyId: string, newStage: PipelineStage) => Promise<void>;
    onClose: () => void;
    colors: {
        background: string;
        backgroundLight: string;
        textPrimary: string;
        textSecondary: string;
        textMuted: string;
        border: string;
        accent: string;
    };
}

const STAGE_CONFIG: { id: PipelineStage; label: string; color: string }[] = [
    { id: 'interested', label: 'Interested', color: '#3B82F6' },
    { id: 'researching', label: 'Researching', color: '#8B5CF6' },
    { id: 'drive_by', label: 'Drive-by', color: '#F59E0B' },
    { id: 'title', label: 'Title', color: '#EC4899' },
    { id: 'offer', label: 'Offer', color: '#10B981' },
    { id: 'under_contract', label: 'Under Contract', color: '#06B6D4' },
    { id: 'pass', label: 'Pass', color: '#6B7280' },
];

// Defensive default theme - prevents crash if colors prop is undefined
const DEFAULT_COLORS: PipelineBoardProps['colors'] = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    border: '#1C2E42',
    accent: '#3A6A7A',
};

const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || !Number.isFinite(value)) return 'TBD';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

interface NextAction {
    code: string;
    label: string;
    urgency: 'normal' | 'urgent';
    dueInDays?: number;
    ageInDays?: number;
    icon: React.ElementType;
}

export const getDaysUntil = (dateStr: string): number | null => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const d = parseInt(parts[2], 10);
        if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
        const date = new Date(y, m - 1, d);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        return Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

export const getDaysSince = (dateStr: string): number | null => {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        const d = parseInt(parts[2], 10);
        if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
        const date = new Date(y, m - 1, d);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        date.setHours(0, 0, 0, 0);
        return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
};

export const getNextAction = (property: PipelineProperty): NextAction | null => {
    const { stage, status, auctionDate, caseFiledDate } = property;
    const daysUntilSale = auctionDate ? getDaysUntil(auctionDate) : null;

    if (status === 'Cancelled') {
        return { code: 'cancelled', label: 'Auction cancelled', urgency: 'normal', icon: X };
    }

    if (status === 'Postponed') {
        return { code: 'postponed', label: 'Auction postponed', urgency: 'normal', icon: Clock };
    }

    // Sale countdown - integrates deadline awareness
    if (daysUntilSale !== null) {
        if (daysUntilSale < 0) {
            return {
                code: 'sale_passed',
                label: 'Sale date passed',
                urgency: 'urgent',
                dueInDays: daysUntilSale,
                icon: Gavel,
            };
        }
        if (daysUntilSale <= 7) {
            return {
                code: 'sale_imminent',
                label: daysUntilSale === 0 ? 'Sale today' : `Sale in ${daysUntilSale} days`,
                urgency: 'urgent',
                dueInDays: daysUntilSale,
                icon: Gavel,
            };
        }
        if (daysUntilSale <= 30) {
            return {
                code: 'sale_countdown',
                label: `Sale in ${daysUntilSale} days`,
                urgency: 'normal',
                dueInDays: daysUntilSale,
                icon: Gavel,
            };
        }
    }

    // Filing age - only when no sale date yet
    if (!auctionDate && caseFiledDate) {
        const age = getDaysSince(caseFiledDate);
        if (age !== null && age >= 45) {
            return {
                code: 'filing_old',
                label: 'Filing 45+ days old',
                urgency: 'normal',
                ageInDays: age,
                icon: Clock,
            };
        }
    }

    switch (stage) {
        case 'interested':
            return {
                code: 'begin_research',
                label: 'Begin research',
                urgency: 'normal',
                icon: Search,
            };
        case 'researching':
            return {
                code: 'drive_by_pending',
                label: 'Drive-by pending',
                urgency: 'normal',
                icon: Car,
            };
        case 'drive_by':
            return { code: 'order_title', label: 'Order title', urgency: 'normal', icon: FileText };
        case 'title':
            return {
                code: 'prepare_offer',
                label: 'Prepare offer',
                urgency: 'normal',
                icon: HeartHandshake,
            };
        case 'offer':
            return {
                code: 'awaiting_result',
                label: 'Awaiting auction result',
                urgency: 'normal',
                icon: Gavel,
            };
        case 'under_contract':
            return {
                code: 'close_deal',
                label: 'Close deal',
                urgency: 'normal',
                icon: HeartHandshake,
            };
        case 'pass':
            return null;
        default:
            return null;
    }
};

export const PipelineBoard: React.FC<PipelineBoardProps> = ({
    properties,
    onStageChange,
    onClose,
    colors: colorsProp,
}) => {
    // Defensive: use default colors if prop is undefined to prevent crash
    const colors = colorsProp ?? DEFAULT_COLORS;

    const [localProperties, setLocalProperties] = useState<PipelineProperty[]>(properties);
    const [isUpdating, setIsUpdating] = useState<string | null>(null);

    useEffect(() => {
        setLocalProperties(properties);
    }, [properties]);

    const handleDragEnd = useCallback(
        async (result: DropResult) => {
            const { destination, source, draggableId } = result;

            if (!destination) return;

            if (
                destination.droppableId === source.droppableId &&
                destination.index === source.index
            ) {
                return;
            }

            const newStage = destination.droppableId as PipelineStage;
            const propertyId = draggableId;

            setLocalProperties(prev =>
                prev.map(p =>
                    p.propertyId === propertyId
                        ? { ...p, stage: newStage, updatedAt: new Date().toISOString() }
                        : p
                )
            );

            setIsUpdating(propertyId);
            try {
                await onStageChange(propertyId, newStage);
            } catch (error) {
                console.error('Failed to update stage:', error);
                setLocalProperties(prev =>
                    prev.map(p =>
                        p.propertyId === propertyId
                            ? { ...p, stage: source.droppableId as PipelineStage }
                            : p
                    )
                );
            } finally {
                setIsUpdating(null);
            }
        },
        [onStageChange]
    );

    const getPropertiesByStage = (stage: PipelineStage): PipelineProperty[] => {
        return localProperties.filter(p => p.stage === stage);
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    height: '64px',
                    background: colors.backgroundLight,
                    borderBottom: `1px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 1.5rem',
                }}
            >
                <h1
                    style={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: colors.textPrimary,
                    }}
                >
                    Pipeline Board
                    <span
                        style={{
                            marginLeft: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: colors.textMuted,
                        }}
                    >
                        ({localProperties.length} properties)
                    </span>
                </h1>
                <button
                    onClick={onClose}
                    style={{
                        padding: '0.5rem',
                        background: 'transparent',
                        border: 'none',
                        color: colors.textSecondary,
                        cursor: 'pointer',
                    }}
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div
                style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '1.5rem',
                    background: colors.background,
                }}
            >
                <DragDropContext onDragEnd={handleDragEnd}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '1rem',
                            minWidth: 'max-content',
                        }}
                    >
                        {STAGE_CONFIG.map(stage => {
                            const stageProperties = getPropertiesByStage(stage.id);
                            return (
                                <Droppable key={stage.id} droppableId={stage.id}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{
                                                width: '280px',
                                                minHeight: '500px',
                                                background: snapshot.isDraggingOver
                                                    ? `${stage.color}10`
                                                    : colors.backgroundLight,
                                                borderRadius: '0.75rem',
                                                border: `1px solid ${snapshot.isDraggingOver ? stage.color : colors.border}`,
                                                padding: '1rem',
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    marginBottom: '1rem',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            background: stage.color,
                                                        }}
                                                    />
                                                    <h3
                                                        style={{
                                                            fontSize: '0.875rem',
                                                            fontWeight: 600,
                                                            color: colors.textPrimary,
                                                        }}
                                                    >
                                                        {stage.label}
                                                    </h3>
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        color: stage.color,
                                                        background: `${stage.color}20`,
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '9999px',
                                                    }}
                                                >
                                                    {stageProperties.length}
                                                </span>
                                            </div>

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.75rem',
                                                }}
                                            >
                                                {stageProperties.map((property, index) => (
                                                    <Draggable
                                                        key={property.propertyId}
                                                        draggableId={property.propertyId}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                style={{
                                                                    background: colors.background,
                                                                    borderRadius: '0.5rem',
                                                                    border: `1px solid ${colors.border}`,
                                                                    padding: '0.75rem',
                                                                    cursor: 'grab',
                                                                    opacity:
                                                                        isUpdating ===
                                                                        property.propertyId
                                                                            ? 0.6
                                                                            : 1,
                                                                    transform: snapshot.isDragging
                                                                        ? 'rotate(2deg)'
                                                                        : 'none',
                                                                    boxShadow: snapshot.isDragging
                                                                        ? '0 8px 24px rgba(0, 0, 0, 0.3)'
                                                                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                                    transition:
                                                                        'box-shadow 0.2s ease, opacity 0.2s ease',
                                                                    ...provided.draggableProps
                                                                        .style,
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'flex-start',
                                                                        gap: '0.5rem',
                                                                        marginBottom: '0.5rem',
                                                                    }}
                                                                >
                                                                    <Home
                                                                        style={{
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            color: colors.textMuted,
                                                                            flexShrink: 0,
                                                                            marginTop: '2px',
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <p
                                                                            style={{
                                                                                fontSize:
                                                                                    '0.8125rem',
                                                                                fontWeight: 500,
                                                                                color: colors.textPrimary,
                                                                                lineHeight: 1.3,
                                                                            }}
                                                                        >
                                                                            {property.address}
                                                                        </p>
                                                                        <p
                                                                            style={{
                                                                                fontSize:
                                                                                    '0.6875rem',
                                                                                color: colors.textMuted,
                                                                            }}
                                                                        >
                                                                            {property.city}
                                                                        </p>
                                                                    </div>
                                                                </div>

                                                                {/* Canonical fields: beds, baths, sqft with unknown semantics */}
                                                                {property.canonical && (
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            gap: '0.5rem',
                                                                            fontSize: '0.625rem',
                                                                            color: colors.textSecondary,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        {(() => {
                                                                            const beds =
                                                                                formatField(
                                                                                    property
                                                                                        .canonical
                                                                                        .beds,
                                                                                    v => `${v}`
                                                                                );
                                                                            return (
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: '0.125rem',
                                                                                        opacity:
                                                                                            beds.isKnown
                                                                                                ? 1
                                                                                                : 0.6,
                                                                                    }}
                                                                                    title={
                                                                                        beds.tooltip
                                                                                    }
                                                                                >
                                                                                    <Bed
                                                                                        style={{
                                                                                            width: '10px',
                                                                                            height: '10px',
                                                                                        }}
                                                                                    />
                                                                                    <span>
                                                                                        {
                                                                                            beds.displayValue
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                        {(() => {
                                                                            const baths =
                                                                                formatField(
                                                                                    property
                                                                                        .canonical
                                                                                        .baths,
                                                                                    formatBaths
                                                                                );
                                                                            return (
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: '0.125rem',
                                                                                        opacity:
                                                                                            baths.isKnown
                                                                                                ? 1
                                                                                                : 0.6,
                                                                                    }}
                                                                                    title={
                                                                                        baths.tooltip
                                                                                    }
                                                                                >
                                                                                    <Bath
                                                                                        style={{
                                                                                            width: '10px',
                                                                                            height: '10px',
                                                                                        }}
                                                                                    />
                                                                                    <span>
                                                                                        {
                                                                                            baths.displayValue
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                        {(() => {
                                                                            const sqft =
                                                                                formatField(
                                                                                    property
                                                                                        .canonical
                                                                                        .living_sqft,
                                                                                    formatSqft
                                                                                );
                                                                            return (
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: '0.125rem',
                                                                                        opacity:
                                                                                            sqft.isKnown
                                                                                                ? 1
                                                                                                : 0.6,
                                                                                    }}
                                                                                    title={
                                                                                        sqft.tooltip
                                                                                    }
                                                                                >
                                                                                    <Square
                                                                                        style={{
                                                                                            width: '10px',
                                                                                            height: '10px',
                                                                                        }}
                                                                                    />
                                                                                    <span>
                                                                                        {
                                                                                            sqft.displayValue
                                                                                        }
                                                                                    </span>
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}

                                                                <div
                                                                    style={{
                                                                        display: 'flex',
                                                                        gap: '0.75rem',
                                                                        fontSize: '0.6875rem',
                                                                        color: colors.textSecondary,
                                                                    }}
                                                                >
                                                                    {property.auctionDate && (
                                                                        <div
                                                                            style={{
                                                                                display: 'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: '0.25rem',
                                                                            }}
                                                                        >
                                                                            <Calendar
                                                                                style={{
                                                                                    width: '10px',
                                                                                    height: '10px',
                                                                                }}
                                                                            />
                                                                            <span>
                                                                                {
                                                                                    property.auctionDate
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {property.openingBid !==
                                                                        undefined && (
                                                                        <div
                                                                            style={{
                                                                                display: 'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: '0.25rem',
                                                                            }}
                                                                        >
                                                                            <DollarSign
                                                                                style={{
                                                                                    width: '10px',
                                                                                    height: '10px',
                                                                                }}
                                                                            />
                                                                            <span>
                                                                                {formatCurrency(
                                                                                    property.openingBid
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {property.status && (
                                                                    <div
                                                                        style={{
                                                                            marginTop: '0.5rem',
                                                                            fontSize: '0.625rem',
                                                                            fontWeight: 500,
                                                                            color:
                                                                                property.status ===
                                                                                'Scheduled'
                                                                                    ? '#10B981'
                                                                                    : property.status ===
                                                                                        'Postponed'
                                                                                      ? '#F59E0B'
                                                                                      : property.status ===
                                                                                          'Cancelled'
                                                                                        ? '#EF4444'
                                                                                        : colors.textMuted,
                                                                            textTransform:
                                                                                'uppercase',
                                                                            letterSpacing: '0.05em',
                                                                        }}
                                                                    >
                                                                        {property.status}
                                                                    </div>
                                                                )}

                                                                {(() => {
                                                                    const action =
                                                                        getNextAction(property);
                                                                    if (!action) return null;
                                                                    const ActionIcon = action.icon;
                                                                    return (
                                                                        <div
                                                                            style={{
                                                                                marginTop:
                                                                                    '0.375rem',
                                                                                display: 'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: '0.25rem',
                                                                                fontSize:
                                                                                    '0.625rem',
                                                                                color:
                                                                                    action.urgency ===
                                                                                    'urgent'
                                                                                        ? '#EF4444'
                                                                                        : colors.textMuted,
                                                                                fontWeight:
                                                                                    action.urgency ===
                                                                                    'urgent'
                                                                                        ? 500
                                                                                        : 400,
                                                                            }}
                                                                        >
                                                                            <ActionIcon
                                                                                style={{
                                                                                    width: '10px',
                                                                                    height: '10px',
                                                                                    flexShrink: 0,
                                                                                }}
                                                                            />
                                                                            <span>
                                                                                {action.label}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>

                                            {stageProperties.length === 0 && (
                                                <div
                                                    style={{
                                                        textAlign: 'center',
                                                        padding: '2rem 1rem',
                                                        color: colors.textMuted,
                                                    }}
                                                >
                                                    <MapPin
                                                        style={{
                                                            width: '24px',
                                                            height: '24px',
                                                            margin: '0 auto 0.5rem',
                                                            opacity: 0.5,
                                                        }}
                                                    />
                                                    <p style={{ fontSize: '0.75rem' }}>
                                                        Drop properties here
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            );
                        })}
                    </div>
                </DragDropContext>
            </div>
        </div>
    );
};

export default PipelineBoard;
